import { useEffect, useMemo, useState } from 'react';
import { CORPUS, corpusEntry } from '../../data/chorales/corpusIndex';
import type { TuneSummary } from '../../data/chorales/corpusTypes';
import { chordName } from '../../data/chorales/derive';
import { TUNE_NOTES } from '../../data/chorales/editorial';
import { chordFamily } from '../../data/observatory';
import { RoomSetting, TuneRoom, tuneRoom } from '../../data/tunes';
import { Key, keyName, parseKey } from '../../music/key';
import { midiToPitch, pcName, transposePitch } from '../../music/pitch';
import { formatRoman } from '../../music/roman';
import type { PlayNote } from '../../audio/player';
import { CHORD_WORDS } from '../copy';
import { usePlayer, usePrefs, useWidth } from '../hooks';
import { PlayIcon } from '../components/Icons';
import { PlayButton } from '../components/PlayButton';
import { Crumbs } from '../components/Shell';
import { CompareTabs } from './Compare';

/* ---------- Shared bits ---------- */

const tensionColor = (v: number) => `color-mix(in oklab, var(--tension) ${Math.round(Math.min(1, Math.max(0, v)) * 100)}%, var(--rest))`;
const feelingWord = (v: number) => (v < 0.15 ? 'at rest' : v < 0.4 ? 'leaning' : v < 0.7 ? 'pulling' : 'urgent');
const displayName = (slug: string, name: string) => TUNE_NOTES[slug]?.name ?? name;

/** Diatonic steps that go with a shift in semitones (for spelling a transposed chord or key). */
const stepsFor = (semis: number) => Math.round((semis * 7) / 12);
function moveKey(k: Key, semis: number): Key {
  const p = transposePitch({ ...k.tonic, octave: 4 }, stepsFor(semis), semis);
  return { tonic: { letter: p.letter, alter: p.alter }, mode: k.mode };
}

/** How much the settings disagree, note by note: a strip of cells, darker where they differ more. */
function DistinctStrip({ distinct, settings, width, height = 14, selected, fluid }: { distinct: number[]; settings: number; width: number; height?: number; selected?: number; fluid?: boolean }) {
  const w = width / distinct.length;
  return (
    <svg width={fluid ? '100%' : width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true" style={{ display: 'block' }}>
      {distinct.map((d, i) => (
        <rect
          key={i}
          x={i * w + 0.5}
          y={0}
          width={Math.max(0.5, w - 1)}
          height={height}
          rx={1.5}
          style={{ fill: 'var(--tension)', opacity: settings > 1 ? 0.08 + (0.85 * (d - 1)) / (settings - 1) : 0.08 }}
          stroke={i === selected ? 'var(--ink)' : 'none'}
        />
      ))}
    </svg>
  );
}

/* ---------- Index ---------- */

export function TunesIndex() {
  const [sort, setSort] = useState<'settings' | 'variety' | 'name'>('settings');
  const tunes = useMemo(() => {
    const list = [...CORPUS.tunes];
    if (sort === 'variety') list.sort((a, b) => b.variety - a.variety || a.name.localeCompare(b.name));
    if (sort === 'name') list.sort((a, b) => displayName(a.slug, a.name).localeCompare(displayName(b.slug, b.name)));
    return list;
  }, [sort]);
  const settings = CORPUS.tunes.reduce((a, t) => a + t.ids.length, 0);
  return (
    <div>
      <header className="page-head">
        <span className="label">Compare</span>
        <h1>One tune, many harmonies</h1>
        <p>
          Bach harmonized the same hymn tunes again and again: {CORPUS.tunes.length} tunes, {settings} settings. Each room lines the settings up under one melody so
          you can hear the same note turn bright, dark, restless or calm.
        </p>
        <p className="hint">The strip on each card marks where the settings disagree: the darker, the more different harmonies under that note.</p>
      </header>
      <CompareTabs current="tunes" />
      <a className="enter" href="#passion" style={{ marginTop: 0, marginBottom: 18 }}>
        <div>
          <span className="label">Start here</span>
          <h2>The Passion Chorale in the St Matthew Passion</h2>
          <p>Five settings of one melody in one work, lined up beat by beat.</p>
        </div>
      </a>
      <div className="library-tools">
        <div className="seg" role="group" aria-label="Order">
          <button aria-pressed={sort === 'settings'} onClick={() => setSort('settings')}>
            Most settings
          </button>
          <button aria-pressed={sort === 'variety'} onClick={() => setSort('variety')}>
            Most varied
          </button>
          <button aria-pressed={sort === 'name'} onClick={() => setSort('name')}>
            A–Z
          </button>
        </div>
      </div>
      <div className="path-grid">
        {tunes.map((t) => (
          <TuneCard key={t.slug} t={t} />
        ))}
      </div>
      <p className="foot">
        Settings are grouped by the tune named in the When in Rome metadata and lined up by an alignment of their melodies; a setting that follows less than 70% of the
        reference melody would be listed as a different version. See Method.
      </p>
    </div>
  );
}

function TuneCard({ t }: { t: TuneSummary }) {
  const keys = [...new Set(t.ids.map((id) => corpusEntry(id)?.homeKey).filter(Boolean) as string[])].map((k) => keyName(parseKey(k)));
  return (
    <a className="path-card" href={`#tune.${t.slug}`}>
      <h3 style={{ fontSize: '1.05rem' }}>{displayName(t.slug, t.name)}</h3>
      <span className="muted" style={{ fontSize: '0.8rem' }}>
        {t.ids.length} settings · {keys.slice(0, 3).join(', ')}
        {keys.length > 3 ? '…' : ''}
      </span>
      <span className="glyph" style={{ display: 'block', minWidth: 0 }} aria-label={`Settings differ on ${Math.round(t.variety * 100)}% of the melody`}>
        <DistinctStrip distinct={t.distinct} settings={t.ids.length} width={200} fluid />
        <span className="muted" style={{ fontSize: '0.74rem', display: 'block', marginTop: 4 }}>
          {t.variety < 0.02 ? 'Harmonized alike every time' : `Harmonies differ on ${Math.round(t.variety * 100)}% of the notes`}
        </span>
      </span>
    </a>
  );
}

/* ---------- Room ---------- */

interface Segment {
  setting: RoomSetting;
  start: number;
  end: number;
  offset: number;
}

function excerpt(s: RoomSetting, from: number, to: number, offset: number, shift: number): PlayNote[] {
  return s.piece.notes
    .filter((n) => n.onset < to - 1e-6 && n.onset + n.dur > from + 1e-6)
    .map((n) => {
      const a = Math.max(n.onset, from);
      const b = Math.min(n.onset + n.dur, to);
      return { onset: offset + a - from, dur: b - a, midi: n.midi + shift, line: n.line, velocity: n.line === 3 ? 0.8 : n.line === 0 ? 0.78 : 0.6 };
    });
}

export function TuneRoomView({ slug, at }: { slug: string; at?: string }) {
  const room = useMemo(() => tuneRoom(slug), [slug]);
  if (!room) {
    return (
      <div className="page-head">
        <h1>No such tune room</h1>
        <p>
          <a href="#tunes">See all tunes</a>
        </p>
      </div>
    );
  }
  return <Room key={room.slug} room={room} at={at} />;
}

function Room({ room, at }: { room: TuneRoom; at?: string }) {
  const { prefs } = usePrefs();
  const { depth } = prefs;
  const { player, position, playing } = usePlayer();
  const [samePitch, setSamePitch] = useState(true);
  const hot = useMemo(
    () =>
      room.distinct
        .map((d, i) => ({ d, i }))
        .sort((a, b) => b.d - a.d || a.i - b.i)
        .slice(0, 5)
        .map((x) => x.i)
        .sort((a, b) => a - b),
    [room],
  );
  const initial = at && /^\d+$/.test(at) ? Math.min(room.columns - 1, Number(at) - 1) : (hot[0] ?? 0);
  const [col, setCol] = useState(initial);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [stripRef, stripWidth] = useWidth<HTMLDivElement>();
  useEffect(() => player.setTempo(prefs.tempo), [player, prefs.tempo]);

  const shiftOf = (s: RoomSetting) => (samePitch ? s.shift : 0);
  const play = (segs: Array<{ setting: RoomSetting; start: number; end: number }>, gap: number) => {
    let t = 0;
    const out: Segment[] = [];
    const notes: PlayNote[] = [];
    for (const s of segs) {
      notes.push(...excerpt(s.setting, s.start, s.end, t, shiftOf(s.setting)));
      out.push({ ...s, offset: t });
      t += s.end - s.start + gap;
    }
    setSegments(out);
    player.setNotes(notes, Math.max(0, t - gap));
    void player.play(0);
  };
  const playColumn = (c: number) =>
    play(
      room.settings
        .filter((s) => s.cells[c].note !== null)
        .map((s) => {
          const cell = s.cells[c];
          return { setting: s, start: Math.max(0, cell.onset - 1), end: cell.onset + Math.max(1, cell.dur) };
        }),
      0.6,
    );
  const playLine = (li: number) => {
    const [a, b] = room.lines[li];
    play(
      room.settings
        .map((s) => {
          const cells = s.cells.slice(a, b + 1).filter((c) => c.note !== null);
          if (!cells.length) return null;
          const last = cells[cells.length - 1];
          return { setting: s, start: cells[0].onset, end: last.onset + last.dur };
        })
        .filter((x): x is { setting: RoomSetting; start: number; end: number } => x !== null),
      1,
    );
  };
  const pick = (c: number) => {
    setCol(c);
    playColumn(c);
  };

  const current = playing ? [...segments].reverse().find((s) => position >= s.offset - 1e-6) : undefined;
  const playingCol = (() => {
    if (!current) return -1;
    const t = current.start + (position - current.offset);
    let found = -1;
    current.setting.cells.forEach((c) => {
      if (c.note !== null && c.onset <= t + 1e-6) found = c.col;
    });
    return found;
  })();

  const W = stripWidth || 640;
  const cw = W / room.columns;
  const ref = room.reference;
  const refMel = ref.melody;
  const lo = Math.min(...refMel.map((m) => m.midi)) - 1;
  const hi = Math.max(...refMel.map((m) => m.midi)) + 1;
  const melH = 64;
  const lineOf = (c: number) => room.lines.findIndex(([a, b]) => c >= a && c <= b);
  const note = pcName(midiToPitch(refMel[col].midi));
  const tuneNote = TUNE_NOTES[room.slug]?.note;

  const chordLabel = (s: RoomSetting) => {
    const cell = s.cells[col];
    if (!cell.event) return 'no note here';
    const ev = cell.event;
    if (depth === 0) return feelingWord(cell.tension);
    const fam = chordFamily(ev.spec.rn);
    const awayFromHome = ev.key.mode !== parseKey(s.piece.homeKey).mode || keyName(ev.key) !== keyName(parseKey(s.piece.homeKey));
    const word = (fam === 'I' || fam === 'i') && awayFromHome ? 'a new home' : CHORD_WORDS[fam];
    if (depth === 1) return word ?? feelingWord(cell.tension);
    const shift = shiftOf(s);
    const root = transposePitch({ ...ev.chord.root, octave: 4 }, stepsFor(shift), shift);
    const name = chordName({ ...ev.chord, root: { letter: root.letter, alter: root.alter } });
    return depth >= 3 ? `${name} · ${formatRoman(ev.spec.rn)} in ${keyName(moveKey(ev.key, shift))}` : `${name}${word ? ` (${word})` : ''}`;
  };

  return (
    <div className="passion tune-room">
      <Crumbs items={[{ href: '#compare', label: 'Compare' }, { href: '#tunes', label: 'Same tune, new harmony' }, { label: displayName(room.slug, room.name) }]} />
      <header className="page-head">
        <span className="label">
          One tune, {room.settings.length} harmonies
        </span>
        <h1>{displayName(room.slug, room.name)}</h1>
        <p>
          {tuneNote ? `${tuneNote} ` : ''}Every setting below sings this melody. Pick a note to hear it in every setting, one after another
          {samePitch ? ', all at the same pitch' : ''}.
        </p>
        <p className="hint">Colour shows the pull of the harmony: cool at rest, warm when it strains. The settings are lined up under the version in {ref.piece.catalogue}.</p>
      </header>

      <div className="transport" style={{ marginBottom: 12 }}>
        <PlayButton playing={playing} label={`Hear note ${col + 1} in all ${room.settings.length} settings`} onPause={() => player.pause()} onPlay={() => playColumn(col)} />
        <span className="field">Whole phrase:</span>
        <div className="seg" role="group" aria-label="Hear a whole phrase in every setting">
          {room.lines.map((_, i) => (
            <button key={i} onClick={() => playLine(i)} title={`Hear phrase ${i + 1} in every setting`} aria-label={`Phrase ${i + 1}`}>
              {i + 1}
            </button>
          ))}
        </div>
        <span className="grow" />
        <label className="field">
          <input type="checkbox" checked={samePitch} onChange={(e) => setSamePitch(e.target.checked)} /> Same pitch for all
        </label>
      </div>

      <div className="chips hot-chips" role="group" aria-label="Where the settings differ most">
        <span className="label">Where they differ most</span>
        {hot.map((c) => (
          <button key={c} className="chip" aria-pressed={c === col} onClick={() => pick(c)}>
            Phrase {lineOf(c) + 1}, note {c - room.lines[Math.max(0, lineOf(c))][0] + 1} <span className="muted">· {room.distinct[c]} harmonies</span>
          </button>
        ))}
      </div>

      <div className="panel grid-scroll" style={{ padding: '14px 16px', ['--cols' as string]: room.columns }}>
        <div className="passion-row">
          <div className="passion-label">
            <span className="label">The melody</span>
          </div>
          <div ref={stripRef} className="passion-strip">
            {stripWidth > 0 && (
              <svg width={W} height={melH + 18} viewBox={`0 0 ${W} ${melH + 18}`} role="img" aria-label="The shared melody; darker marks below show where the settings differ">
                {room.lines.map(([a], i) => (
                  <line key={i} x1={a * cw} x2={a * cw} y1={0} y2={melH} className="s-grid" />
                ))}
                <rect x={col * cw} y={0} width={cw} height={melH + 18} className="col-mark" />
                {refMel.map((m, i) => {
                  const y = 6 + ((hi - m.midi) / (hi - lo)) * (melH - 16);
                  return (
                    <g key={i} onClick={() => pick(i)} style={{ cursor: 'pointer' }}>
                      <rect x={i * cw} y={0} width={cw} height={melH} fill="transparent" />
                      <line x1={i * cw + 1} x2={(i + 1) * cw - 1} y1={y} y2={y} className={i === col || i === playingCol ? 's-tension' : 's-ink'} strokeWidth={3} strokeLinecap="round" />
                      {m.fermata && (
                        <text x={i * cw + cw / 2} y={melH - 2} textAnchor="middle" className="t-mono">
                          𝄐
                        </text>
                      )}
                    </g>
                  );
                })}
                <g transform={`translate(0, ${melH + 4})`}>
                  <DistinctStrip distinct={room.distinct} settings={room.settings.length} width={W} height={10} />
                </g>
              </svg>
            )}
          </div>
        </div>

        {room.settings.map((s, ri) => {
          const isPlayingRow = current?.setting.id === s.id;
          const entry = corpusEntry(s.id);
          return (
            <div className="passion-row" key={s.id}>
              <div className="passion-label">
                <button className="btn quiet" style={{ height: 30, padding: '0 8px', marginLeft: -8 }} onClick={() => play([{ setting: s, start: 0, end: s.view.length }], 0)} aria-label={`Play ${s.piece.title}`}>
                  <PlayIcon />
                </button>
                <div style={{ minWidth: 0 }}>
                  <a href={`#${s.id}`} className="setting-title">
                    {s.piece.title}
                  </a>
                  <span className="muted" style={{ fontSize: '0.76rem' }}>
                    {s.piece.catalogue} · {keyName(parseKey(entry?.homeKey ?? s.piece.homeKey))}
                    {ri === 0 ? ' · reference' : s.agreement < 0.995 ? ` · follows ${Math.round(s.agreement * 100)}%` : ''}
                  </span>
                </div>
              </div>
              <div className="passion-strip">
                <svg width={W} height={30} viewBox={`0 0 ${W} 30`} role="img" aria-label={`Harmony of ${s.piece.title}, note by note`}>
                  {s.cells.map((c) => (
                    <g key={c.col} onClick={() => pick(c.col)} style={{ cursor: 'pointer' }}>
                      <title>{c.event ? `Note ${c.col + 1}: ${chordName(c.event.chord)} (${formatRoman(c.event.spec.rn)} in ${keyName(c.event.key)})` : `Note ${c.col + 1}: not sung in this setting`}</title>
                      {c.event ? (
                        <rect
                          x={c.col * cw + 0.5}
                          y={2}
                          width={Math.max(0.5, cw - 1)}
                          height={26}
                          rx={2}
                          style={{ fill: tensionColor(c.tension), opacity: 0.35 + c.tension * 0.55 }}
                          stroke={c.col === col ? 'var(--ink)' : isPlayingRow && c.col === playingCol ? 'var(--tension)' : 'none'}
                          strokeWidth={2}
                        />
                      ) : (
                        <line x1={c.col * cw + 1} x2={(c.col + 1) * cw - 1} y1={15} y2={15} className="s-grid" />
                      )}
                    </g>
                  ))}
                  {room.lines.slice(1).map(([a]) => (
                    <line key={a} x1={a * cw} x2={a * cw} y1={0} y2={30} className="s-ink" strokeWidth={1.2} />
                  ))}
                </svg>
              </div>
            </div>
          );
        })}
      </div>

      <section className="panel" style={{ marginTop: 14 }} aria-live="polite">
        <div className="panel-head">
          <span className="label">
            Phrase {lineOf(col) + 1} · note {col + 1} of {room.columns} · melody {note}
            {samePitch ? ' (common pitch)' : ` (in ${ref.piece.catalogue})`} · {room.distinct[col]} different {room.distinct[col] === 1 ? 'harmony' : 'harmonies'}
          </span>
          <span style={{ display: 'flex', gap: 6 }}>
            <button className="btn" onClick={() => setCol((c) => Math.max(0, c - 1))} aria-label="Previous note">
              ←
            </button>
            <button className="btn" onClick={() => setCol((c) => Math.min(room.columns - 1, c + 1))} aria-label="Next note">
              →
            </button>
          </span>
        </div>
        <div className="beat-table">
          {room.settings.map((s) => {
            const cell = s.cells[col];
            return (
              <div className="beat-row" key={s.id}>
                <button
                  className="btn quiet"
                  style={{ height: 30, padding: '0 8px' }}
                  disabled={!cell.event}
                  onClick={() => play([{ setting: s, start: Math.max(0, cell.onset - 1), end: cell.onset + Math.max(1, cell.dur) }], 0)}
                  aria-label={`Hear this note in ${s.piece.title}`}
                >
                  <PlayIcon />
                </button>
                <span className="beat-title">
                  {s.piece.title} <span className="muted beat-cat">{s.piece.catalogue}</span>
                </span>
                <span className="beat-chord">{chordLabel(s)}</span>
                <span className="beat-meter" aria-label={`tension ${cell.tension.toFixed(2)}`}>
                  <i style={{ width: `${Math.round(cell.tension * 100)}%`, background: tensionColor(cell.tension) }} />
                </span>
                <span className="beat-lyric">{cell.lyric ? cell.lyric.replace(/-$/, '–') : ''}</span>
              </div>
            );
          })}
        </div>
        <p className="muted" style={{ fontSize: '0.84rem', marginTop: 10 }}>
          Settings are lined up by their melodies, not by time: where Bach ornaments the tune, the extra notes are heard but not given a column; where a setting has no
          note for a column, its row is left empty. {depth >= 2 && samePitch ? 'Chord names are given at the common pitch. ' : ''}Harmonic analyses from When in Rome;
          the colour is this site’s tension model.
        </p>
      </section>
      {room.variants.length > 0 && (
        <section className="panel" style={{ marginTop: 14 }}>
          <span className="label">Another version of the tune</span>
          <ul>
            {room.variants.map((v) => (
              <li key={v.id}>
                <a href={`#${v.id}`}>{v.piece.title}</a> {v.piece.catalogue} (follows {Math.round(v.agreement * 100)}% of the melody above)
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
